import { z } from 'zod';
import { Api } from '@automapa/svelte-automapa-template';

/** Wiersz listy panelu - odpowiedź UserPermissions::getUsers() (backend zwraca snake_case). */
export const PanelUserSchema = z.object({
    webuser_id: z.number(),
    comment: z.string().nullable(),
    can_read: z.boolean(),
    can_write: z.boolean(),
    is_user_manager: z.boolean(),
    editable: z.boolean(),
});
export type PanelUserType = z.infer<typeof PanelUserSchema>;

export const PanelUserListSchema = z.array(PanelUserSchema);

export const SetPermissionsRequestSchema = z.object({
    webuser_id: z.number(),
    can_read: z.boolean(),
    can_write: z.boolean(),
    is_user_manager: z.boolean(),
});
export type SetPermissionsRequestType = z.infer<typeof SetPermissionsRequestSchema>;

/**
 * Żądanie dodania użytkownika. Nie ma tu customer_id - klient bierze się z tokena, a przesłanie go
 * w ciele żądania nie ma wpływu na zapis (backend go ignoruje). Format adresu sprawdza GUI przed
 * wysłaniem oraz backend (marker email_invalid), więc schemat pilnuje tylko niepustej wartości.
 */
export const CreateUserRequestSchema = z.object({
    email: z.string().min(1),
    can_read: z.boolean(),
    can_write: z.boolean(),
    is_user_manager: z.boolean(),
});
export type CreateUserRequestType = z.infer<typeof CreateUserRequestSchema>;

/** Cztery stany kontrolki. Etykiety są kluczami i18n (konwencja projektu: klucz = tekst angielski). */
export type PermissionStateId = 'none' | 'read' | 'read_write' | 'user_manager';

export const permissionStates: { id: PermissionStateId; label: string }[] = [
    { id: 'none', label: 'No access' },
    { id: 'read', label: 'Read only' },
    { id: 'read_write', label: 'Read and write' },
    { id: 'user_manager', label: 'User manager permissions' },
];

/** Flagi wysyłane do backendu; rola wymusza can_read i can_write (CHECK w app_user). */
export type PermissionFlagsType = Pick<PanelUserType, 'can_read' | 'can_write' | 'is_user_manager'>;

/**
 * Rola jest stanem nadrzędnym: wiersz managera zawsze ma can_read i can_write, więc pokazujemy
 * `user_manager` niezależnie od pozostałych flag. `is_user_manager` jest opcjonalne, żeby dało się
 * wyliczyć stan z samej pary flag dostępu.
 */
export function permissionStateOf(
    user: Pick<PanelUserType, 'can_read' | 'can_write'> & { is_user_manager?: boolean }
): PermissionStateId {
    if (user.is_user_manager) {
        return 'user_manager';
    }

    if (!user.can_read) {
        return 'none';
    }

    return user.can_write ? 'read_write' : 'read';
}

export function permissionStateToFlags(state: PermissionStateId): PermissionFlagsType {
    switch (state) {
        case 'user_manager':
            return { can_read: true, can_write: true, is_user_manager: true };
        case 'read_write':
            return { can_read: true, can_write: true, is_user_manager: false };
        case 'read':
            return { can_read: true, can_write: false, is_user_manager: false };
        default:
            return { can_read: false, can_write: false, is_user_manager: false };
    }
}

/** Filtr listy po comment, wyłącznie po stronie frontu - lista jest pobierana raz. */
export function filterPanelUsers(users: PanelUserType[], query: string): PanelUserType[] {
    const needle = query.trim().toLowerCase();
    if (needle === '') {
        return users;
    }

    return users.filter(user => (user.comment ?? '').toLowerCase().includes(needle));
}

/**
 * Ukrycie kont bez dostępu (can_read = false) - konta osób, które odeszły z firmy, zostają w
 * app_user na potrzeby historii, ale na co dzień tylko zaśmiecają listę. Filtr działa na już
 * pobranej liście, bez dodatkowego żądania. Własny wiersz managera ma can_read = true (rola
 * wymusza oba dostępy), więc ten filtr nigdy go nie ukrywa.
 */
export function filterUsersWithAccess(
    users: PanelUserType[],
    showWithoutAccess: boolean
): PanelUserType[] {
    if (showWithoutAccess) {
        return users;
    }

    return users.filter(user => user.can_read);
}

/**
 * Złożenie obu filtrów panelu. Każdy patrzy na inne pole wiersza i żaden nie zmienia kolejności,
 * więc wynik nie zależy od kolejności złożenia.
 */
export function visiblePanelUsers(
    users: PanelUserType[],
    query: string,
    showWithoutAccess: boolean
): PanelUserType[] {
    return filterUsersWithAccess(filterPanelUsers(users, query), showWithoutAccess);
}

/**
 * Markery błędów 403 z backendu (PermissionGuard) i odpowiadające im klucze i18n.
 */
const permissionErrorMessages: Record<string, string> = {
    account_blocked: 'Your account has been blocked',
    read_only: 'Your account has read-only access',
    not_user_manager: 'You are not allowed to manage user permissions',
    not_admin: 'You are not allowed to manage user permissions',
    target_outside_customer: 'This user does not belong to your account',
    target_is_self: 'You cannot change your own permissions',
    inconsistent_permissions: 'This combination of permissions is not allowed',
    // odmowy dodania użytkownika (WebuserAccount, UserPermissions::createUser)
    email_invalid: 'Enter a valid e-mail address',
    user_already_added: 'This user has already been added to your account',
    webuser_not_created: 'Failed to create the user account',
};

/**
 * Klucz i18n komunikatu dla odrzuconego żądania.
 *
 * Markera szukamy w TEKŚCIE odpowiedzi, nie w sparsowanym obiekcie: przy odmowach z metod
 * statycznych gateway renderuje ciało błędu dwa razy (`{...}{...}`), więc odpowiedź nie jest
 * poprawnym JSON-em i axios zostawia ją jako string. Api.getData() odrzuca wtedy pełnym błędem
 * axiosa, bo backend zwraca `translationId`, a vendor szuka `translation_id`.
 */
export function permissionErrorKey(error: unknown): string {
    const data = (error as { response?: { data?: unknown } })?.response?.data;
    const text = typeof data === 'string' ? data : JSON.stringify(data ?? '');
    const marker = Object.keys(permissionErrorMessages).find(key => text.includes(key));

    return marker ? permissionErrorMessages[marker] : 'Failed to save permissions';
}

export class UserPermissionsApi {
    public static async getUsers(api: Api): Promise<PanelUserType[]> {
        return api.getData<any, PanelUserType[]>(
            'post',
            `/user_permissions/get_users`,
            {},
            z.object({}),
            PanelUserListSchema
        );
    }

    public static async setPermissions(
        api: Api,
        webuserId: number,
        state: PermissionStateId
    ): Promise<PanelUserType> {
        const request: SetPermissionsRequestType = {
            webuser_id: webuserId,
            ...permissionStateToFlags(state),
        };

        return api.getData<SetPermissionsRequestType, PanelUserType>(
            'post',
            `/user_permissions/set_permissions`,
            request,
            SetPermissionsRequestSchema,
            PanelUserSchema
        );
    }

    /**
     * Dodanie użytkownika do klienta wywołującego. Flagi liczy permissionStateToFlags(), więc GUI
     * podaje samą rolę początkową i nie może wysłać niespójnej kombinacji (np. managera bez zapisu,
     * odrzucanej przez CHECK w app_user). Odpowiedź to gotowy wiersz panelu - lista dokłada go
     * u siebie bez ponownego pobrania całości.
     */
    public static async createUser(
        api: Api,
        email: string,
        state: PermissionStateId
    ): Promise<PanelUserType> {
        const request: CreateUserRequestType = {
            email: email.trim(),
            ...permissionStateToFlags(state),
        };

        return api.getData<CreateUserRequestType, PanelUserType>(
            'post',
            `/user_permissions/create_user`,
            request,
            CreateUserRequestSchema,
            PanelUserSchema
        );
    }
}
